// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { isThemeActive, Theme, useResizeObserver } from '@cloudscape-design/component-toolkit/internal';
import InternalDropdown from '../../../dropdown/internal';
import InternalIcon from '../../../icon/internal';
import InternalInput from '../../../input/internal';
import InternalLiveRegion from '../../../live-region/internal';
import InternalToken from '../../../token/internal';
import { getBaseProps } from '../../base-component';
import { getBreakpointValue } from '../../breakpoints';
import { ResetGroupedControlContext } from '../../context/control-group-context';
import { useFormFieldContext } from '../../context/form-field-context';
import { fireCancelableEvent, fireNonCancelableEvent } from '../../events';
import { useIMEComposition } from '../../hooks/use-ime-composition';
import { KeyCode, KeyCodeDelete } from '../../keycode';
import { getDropdownMinWidth } from '../../utils/get-dropdown-min-width';
import { nodeBelongs } from '../../utils/node-belongs';
import { processAttributes } from '../../utils/with-native-attributes';
import { OverflowDropdown } from './overflow-dropdown';
import { useTokenListFocus } from './use-token-list-focus';
import { useTokenOverflow } from './use-token-overflow';
import styles from './styles.css.js';
import testUtilStyles from './test-classes/styles.css.js';
const AutosuggestInput = React.forwardRef(({ value, onChange, onBlur, onFocus, onKeyUp, onKeyDown, name, placeholder, disabled, readOnly, autoFocus, ariaLabel, ariaRequired, disableBrowserAutocorrect = false, expandToViewport, ariaControls, ariaActivedescendant, clearAriaLabel, dropdownExpanded = true, dropdownContentKey, dropdownContentFocusable = false, dropdownContent = null, dropdownFooter = null, dropdownWidth, loopFocus, nativeInputAttributes, onCloseDropdown, onDelayedInput, onPressArrowDown, onPressArrowUp, onPressEnter, style, tokens, tokenOverflowAriaLabel, tokenInsertedAriaLabel, __internalRootRef, ...restProps }, ref) => {
    const isTokenMode = !!tokens;
    const tokenList = tokens !== null && tokens !== void 0 ? tokens : [];
    const baseProps = getBaseProps(restProps);
    const formFieldContext = useFormFieldContext(restProps);
    const { invalid, warning, ariaLabelledby } = formFieldContext;
    const inputRef = useRef(null);
    const triggerRowRef = useRef(null);
    const tokenListRef = useRef(null);
    const measureRef = useRef(null);
    const overflowPillRef = useRef(null);
    const dropdownContentRef = useRef(null);
    const dropdownFooterRef = useRef(null);
    const preventOpenOnFocusRef = useRef(false);
    const preventCloseOnBlurRef = useRef(false);
    const [triggerWidth, setTriggerWidth] = useState(null);
    const [overflowOpen, setOverflowOpen] = useState(false);
    const [open, setOpen] = useState(false);
    const [tokenOperationAnnouncement, setTokenOperationAnnouncement] = useState('');
    const { containerRef, visibleCount, tokenListMaxWidth } = useTokenOverflow(measureRef, triggerRowRef, tokenList.length, isTokenMode);
    useResizeObserver(() => (isTokenMode ? triggerRowRef.current : inputRef.current), entry => entry.borderBoxWidth > 0 && setTriggerWidth(entry.borderBoxWidth));
    // No dependency array: runs after every render to keep tabIndex in sync with the
    // current DOM state. A deps array would cause a frame of stale tabIndex values
    // after token additions/removals before the next re-render syncs it.
    useLayoutEffect(() => {
        if (!isTokenMode || !tokenListRef.current) {
            return;
        }
        const dismissButtons = tokenListRef.current.querySelectorAll('[data-token-item] button');
        dismissButtons.forEach((btn, idx) => {
            btn.tabIndex = !disabled && idx === 0 && hiddenTokens.length === 0 ? 0 : -1;
        });
    });
    const openDropdown = () => !readOnly && setOpen(true);
    const closeDropdown = () => {
        setOpen(false);
        fireNonCancelableEvent(onCloseDropdown, null);
    };
    const { isComposing } = useIMEComposition(inputRef);
    useImperativeHandle(ref, () => ({
        focus(options) {
            var _a;
            if (options === null || options === void 0 ? void 0 : options.preventDropdown) {
                preventOpenOnFocusRef.current = true;
            }
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        },
        select() {
            var _a;
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.select();
        },
        open: openDropdown,
        close: closeDropdown,
        announceTokenAdded(label) {
            setTokenOperationAnnouncement(buildAnnouncementMessage(label));
        },
    }));
    const buildAnnouncementMessage = (label) => tokenInsertedAriaLabel ? tokenInsertedAriaLabel(label) : `${label} inserted`;
    const addToken = (label) => {
        if (!label.trim()) {
            return;
        }
        const updated = [...tokenList, { value: label.trim(), dismissLabel: label.trim() }];
        setTokenOperationAnnouncement(buildAnnouncementMessage(label.trim()));
        fireNonCancelableEvent(onChange, { value: '', tokens: updated });
    };
    const removeToken = (index, suppressFocus = false) => {
        var _a;
        preventOpenOnFocusRef.current = true;
        const updated = tokenList.filter((_, i) => i !== index);
        fireNonCancelableEvent(onChange, { value, tokens: updated });
        if (suppressFocus) {
            return;
        }
        if (updated.length === 0) {
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
        else {
            const preRemovalVisibleStart = tokenList.length - effectiveVisible;
            const targetSlot = index - preRemovalVisibleStart;
            requestAnimationFrame(() => {
                var _a, _b;
                const dismissButtons = (_a = tokenListRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('[data-token-item] button');
                if (!dismissButtons || dismissButtons.length === 0) {
                    (_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                    return;
                }
                const positionInVisible = Math.max(0, Math.min(targetSlot, dismissButtons.length - 1));
                dismissButtons[positionInVisible].focus();
            });
        }
    };
    const effectiveVisible = visibleCount === undefined ? tokenList.length : visibleCount;
    const visibleTokens = tokenList.slice(tokenList.length - effectiveVisible);
    const hiddenTokens = tokenList.slice(0, tokenList.length - effectiveVisible);
    const { focusTokenAtIndex } = useTokenListFocus({
        tokenListRef,
        inputRef,
        tokenListLength: tokenList.length,
        effectiveVisible,
    });
    const handleBlur = () => {
        if (!preventCloseOnBlurRef.current) {
            closeDropdown();
            fireNonCancelableEvent(onBlur, null);
        }
    };
    const handleFocus = () => {
        if (!preventOpenOnFocusRef.current && !overflowOpen) {
            openDropdown();
            fireNonCancelableEvent(onFocus, null);
        }
        preventOpenOnFocusRef.current = false;
    };
    const fireKeydown = (event) => fireCancelableEvent(onKeyDown, event.detail, event);
    // Shared ESC key handler logic for both input and dropdown elements
    // When returnFocus is true (from dropdown elements), focus
    const handleEscapeKey = (returnFocus) => {
        var _a;
        if (open) {
            closeDropdown();
            if (returnFocus) {
                // Return focus to input when closing from dropdown elements (e.g., recovery button)
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        }
        else if (value) {
            fireNonCancelableEvent(onChange, isTokenMode ? { value: '', tokens: tokenList } : { value: '' });
        }
    };
    const handleKeyDown = (event) => {
        switch (event.detail.keyCode) {
            case KeyCode.down: {
                if (overflowOpen) {
                    break;
                }
                onPressArrowDown === null || onPressArrowDown === void 0 ? void 0 : onPressArrowDown();
                openDropdown();
                event.preventDefault();
                break;
            }
            case KeyCode.up: {
                if (overflowOpen) {
                    break;
                }
                onPressArrowUp === null || onPressArrowUp === void 0 ? void 0 : onPressArrowUp();
                openDropdown();
                event.preventDefault();
                break;
            }
            case KeyCode.enter: {
                if (isComposing()) {
                    event.preventDefault();
                    return;
                }
                if (open) {
                    if (!(onPressEnter === null || onPressEnter === void 0 ? void 0 : onPressEnter())) {
                        closeDropdown();
                    }
                    event.preventDefault();
                }
                else if (isTokenMode && value) {
                    addToken(value);
                    event.preventDefault();
                }
                fireKeydown(event);
                break;
            }
            case KeyCode.backspace: {
                if (!readOnly && !disabled && isTokenMode && value === '' && tokenList.length > 0) {
                    focusTokenAtIndex(tokenList.length - 1);
                    event.preventDefault();
                }
                fireKeydown(event);
                break;
            }
            case KeyCode.escape: {
                // Use shared ESC handler, without focus restoration since ESC came from input
                handleEscapeKey(false);
                if (open || value) {
                    event.stopPropagation();
                }
                event.preventDefault();
                fireKeydown(event);
                break;
            }
            default: {
                fireKeydown(event);
            }
        }
    };
    const handleChange = (val) => {
        openDropdown();
        fireNonCancelableEvent(onChange, isTokenMode ? { value: val, tokens: tokenList } : { value: val });
    };
    const handleDelayedInput = (val) => {
        fireNonCancelableEvent(onDelayedInput, { value: val });
    };
    const handleDropdownMouseDown = event => {
        // Prevent currently focused element from losing focus.
        if (!dropdownContentFocusable) {
            event.preventDefault();
        }
        // Prevent closing dropdown on click inside.
        else {
            preventCloseOnBlurRef.current = true;
            requestAnimationFrame(() => {
                preventCloseOnBlurRef.current = false;
            });
        }
    };
    // Closes dropdown when outside click is detected.
    // Similar to the internal dropdown implementation but includes the target as well.
    useEffect(() => {
        if (!open) {
            return;
        }
        const clickListener = (event) => {
            if (!nodeBelongs(inputRef.current, event.target) &&
                !nodeBelongs(dropdownContentRef.current, event.target) &&
                !nodeBelongs(dropdownFooterRef.current, event.target) &&
                !nodeBelongs(triggerRowRef.current, event.target)) {
                closeDropdown();
            }
        };
        window.addEventListener('mousedown', clickListener);
        return () => window.removeEventListener('mousedown', clickListener);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    const handleDropdownKeyDown = event => {
        // Handle ESC key from focusable elements inside the dropdown (e.g., recovery button)
        // but NOT from the input itself (that's handled by handleKeyDown)
        const isFromInput = event.target === inputRef.current || nodeBelongs(inputRef.current, event.target);
        if (event.key === 'Escape' && !isFromInput) {
            event.stopPropagation();
            event.preventDefault();
            // Use shared ESC handler with focus restoration since ESC came from dropdown element
            handleEscapeKey(true);
        }
    };
    const expanded = open && dropdownExpanded;
    const pillIconName = isThemeActive(Theme.OneTheme) ? 'angle-down' : 'caret-down-filled';
    const pillIconSize = isThemeActive(Theme.OneTheme) ? 'x-small' : 'normal';
    const nativeAttributes = {
        name,
        placeholder,
        autoFocus,
        onClick: openDropdown,
        role: 'combobox',
        'aria-autocomplete': 'list',
        'aria-expanded': expanded,
        'aria-controls': open ? ariaControls : undefined,
        'aria-owns': open ? ariaControls : undefined,
        'aria-label': ariaLabel,
        'aria-activedescendant': ariaActivedescendant,
    };
    const renderDropdownShell = (trigger, wrapContent = false) => (React.createElement(InternalDropdown, { minWidth: getDropdownMinWidth({ expandToViewport, triggerWidth, dropdownWidth }), maxWidth: getBreakpointValue('xxs'), contentKey: dropdownContentKey, onFocus: handleFocus, onBlur: handleBlur, trigger: trigger, onMouseDown: handleDropdownMouseDown, open: open && (!!dropdownContent || !!dropdownFooter), footer: dropdownFooterRef && (React.createElement("div", { ref: dropdownFooterRef, className: styles['dropdown-footer'], onKeyDown: handleDropdownKeyDown }, wrapContent ? (React.createElement(ResetGroupedControlContext, null, open && dropdownFooter ? dropdownFooter : null)) : open && dropdownFooter ? (dropdownFooter) : null)), expandToViewport: expandToViewport, loopFocus: loopFocus, content: open && dropdownContent ? (React.createElement("div", { ref: dropdownContentRef, className: styles['dropdown-content'] }, wrapContent ? (React.createElement(ResetGroupedControlContext, null, dropdownContent)) : (dropdownContent))) : null }));
    if (!isTokenMode) {
        return (React.createElement("div", { ...baseProps, className: clsx(baseProps.className, styles.root, testUtilStyles.root), ref: __internalRootRef }, renderDropdownShell(React.createElement(InternalInput, { type: "visualSearch", value: value, onChange: event => handleChange(event.detail.value), __onDelayedInput: event => handleDelayedInput(event.detail.value), onKeyDown: handleKeyDown, onKeyUp: onKeyUp, disabled: disabled, disableBrowserAutocorrect: disableBrowserAutocorrect, readOnly: readOnly, ariaRequired: ariaRequired, clearAriaLabel: clearAriaLabel, ref: inputRef, autoComplete: false, nativeInputAttributes: processAttributes(nativeAttributes, nativeInputAttributes, 'Autosuggest'), __skipNativeAttributesWarnings: Object.keys(nativeAttributes), style: style, ...formFieldContext }), true)));
    }
    return (React.createElement("div", { ...baseProps, className: clsx(baseProps.className, styles.root, testUtilStyles.root), ref: __internalRootRef, role: "group", "aria-label": ariaLabelledby ? undefined : ariaLabel, "aria-labelledby": ariaLabelledby },
        React.createElement(InternalLiveRegion, { tagName: "span", hidden: true, assertive: true, delay: 1 }, tokenOperationAnnouncement),
        React.createElement("div", { ref: measureRef, className: styles['measurement-container'], "data-measure-container": "true" },
            tokenList.map((token, i) => (React.createElement("span", { key: `measure-${i}`, "data-measure-token": "true", style: { display: 'inline-flex', flexShrink: 0 } },
                React.createElement(InternalToken, { label: token.value, icon: token.icon, variant: "inline", onDismiss: () => undefined })))),
            React.createElement("button", { "data-measure-pill": "true", className: styles['token-overflow-pill'], style: { visibility: 'hidden', pointerEvents: 'none' } },
                `+${Math.max(tokenList.length, 1)}`,
                React.createElement(InternalIcon, { name: pillIconName, size: pillIconSize }))),
        React.createElement("div", { ref: containerRef, className: styles['token-trigger-container'] },
            renderDropdownShell(React.createElement("div", { ref: triggerRowRef, className: clsx(styles['token-trigger'], testUtilStyles['token-trigger'], invalid && styles['token-trigger-invalid'], warning && !invalid && styles['token-trigger-warning'], disabled && styles['token-trigger-disabled'], readOnly && !disabled && styles['token-trigger-readonly']) },
                React.createElement("span", { "data-search-icon": "true", style: {
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0,
                    } },
                    React.createElement(InternalIcon, { name: "search", variant: disabled ? 'disabled' : 'subtle' })),
                tokenList.length > 0 && (React.createElement("ul", { ref: tokenListRef, className: clsx(styles['token-list'], testUtilStyles['token-list']), style: tokenListMaxWidth !== undefined ? { maxInlineSize: tokenListMaxWidth } : undefined },
                    hiddenTokens.length > 0 && (React.createElement("li", { className: clsx(styles['token-list-item'], testUtilStyles['token-list-item']) },
                        React.createElement("button", { ref: overflowPillRef, type: "button", tabIndex: 0, disabled: disabled, className: clsx(styles['token-overflow-pill'], testUtilStyles['token-overflow-pill']), "aria-haspopup": "dialog", "aria-expanded": overflowOpen, "aria-label": tokenOverflowAriaLabel === null || tokenOverflowAriaLabel === void 0 ? void 0 : tokenOverflowAriaLabel(hiddenTokens.length), onFocus: (e) => {
                                var _a;
                                const comingFromOutside = !e.relatedTarget || !((_a = tokenListRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.relatedTarget));
                                if (comingFromOutside) {
                                    preventOpenOnFocusRef.current = true;
                                }
                            }, onKeyDown: (e) => {
                                var _a;
                                if (e.keyCode === KeyCode.right) {
                                    e.preventDefault();
                                    const firstTokenButton = (_a = tokenListRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('[data-token-item] button');
                                    if (firstTokenButton) {
                                        firstTokenButton.focus();
                                    }
                                }
                            }, onClick: () => !disabled && setOverflowOpen(v => !v) },
                            `+${hiddenTokens.length}`,
                            React.createElement("span", { className: clsx(styles['pill-icon'], overflowOpen && styles['pill-icon-open']) },
                                React.createElement(InternalIcon, { name: pillIconName, size: pillIconSize }))))),
                    visibleTokens.map((token, i) => {
                        var _a;
                        const realIndex = tokenList.length - effectiveVisible + i;
                        return (React.createElement("li", { key: realIndex, className: clsx(styles['token-list-item'], testUtilStyles['token-list-item']), "data-token-item": "true", "data-token-index": i + 1, onFocus: (e) => {
                                var _a;
                                const comingFromOutside = !e.relatedTarget || !((_a = tokenListRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.relatedTarget));
                                if (comingFromOutside) {
                                    preventOpenOnFocusRef.current = true;
                                }
                            }, onKeyDown: (e) => {
                                var _a, _b;
                                if (e.keyCode === KeyCode.backspace || e.keyCode === KeyCodeDelete) {
                                    e.preventDefault();
                                    if (!readOnly && !disabled) {
                                        removeToken(realIndex);
                                    }
                                }
                                else if (e.keyCode === KeyCode.left) {
                                    e.preventDefault();
                                    if (i === 0) {
                                        if (hiddenTokens.length > 0) {
                                            (_a = overflowPillRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                                        }
                                    }
                                    else {
                                        focusTokenAtIndex(realIndex - 1);
                                    }
                                }
                                else if (e.keyCode === KeyCode.right) {
                                    e.preventDefault();
                                    if (realIndex < tokenList.length - 1) {
                                        focusTokenAtIndex(realIndex + 1);
                                    }
                                    else {
                                        (_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                                    }
                                }
                            } },
                            React.createElement(InternalToken, { label: token.value, dismissLabel: (_a = token.dismissLabel) !== null && _a !== void 0 ? _a : token.value, icon: token.icon, variant: "inline", disabled: disabled, readOnly: readOnly, onDismiss: () => removeToken(realIndex) })));
                    }))),
                React.createElement("div", { className: styles['token-input-wrapper'], style: { flex: '1 1 0', minInlineSize: '20%', overflow: 'hidden' } },
                    React.createElement(InternalInput, { type: "text", value: value, onChange: event => handleChange(event.detail.value), __onDelayedInput: event => handleDelayedInput(event.detail.value), onKeyDown: handleKeyDown, onKeyUp: onKeyUp, disabled: disabled, disableBrowserAutocorrect: disableBrowserAutocorrect, readOnly: readOnly, ariaRequired: ariaRequired, clearAriaLabel: clearAriaLabel, ref: inputRef, autoComplete: false, nativeInputAttributes: processAttributes(nativeAttributes, nativeInputAttributes, 'Autosuggest'), __skipNativeAttributesWarnings: Object.keys(nativeAttributes), __noBorder: true, ...(!disabled && !readOnly && value
                            ? {
                                __endIcon: 'close',
                                __onEndIconClick: () => {
                                    var _a;
                                    (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                                    fireNonCancelableEvent(onChange, { value: '', tokens: tokenList });
                                },
                            }
                            : {}), ...formFieldContext, invalid: false, warning: false })))),
            hiddenTokens.length > 0 && (React.createElement(OverflowDropdown, { tokens: hiddenTokens, triggerRef: overflowPillRef, open: overflowOpen, readOnly: readOnly, onDismiss: i => {
                    if (hiddenTokens.length === 1) {
                        setOverflowOpen(false);
                        requestAnimationFrame(() => {
                            var _a, _b;
                            const firstButton = (_a = tokenListRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('[data-token-item] button');
                            if (firstButton) {
                                firstButton.focus();
                            }
                            else {
                                (_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                            }
                        });
                    }
                    removeToken(i, true);
                }, onClose: () => {
                    var _a;
                    setOverflowOpen(false);
                    (_a = overflowPillRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                } })))));
});
export default AutosuggestInput;
//# sourceMappingURL=index.js.map